// components/project-details/index.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { httpsCallable } from "firebase/functions";
import { functions } from '../../services/firebase';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
// Archive temporarily disabled until behavior is clarified
// import ArchiveModal from './ArchiveModal';
import PaymentSetupCard from '../payments/PaymentSetupCard';

// Import CSS module
import styles from './ProjectDetails.module.css';

// Import all child components
import ProjectHeader from './ProjectHeader';
import ProjectTimeline from './ProjectTimeline';
import ProjectTabs from './ProjectTabs';
import CustomerInfo from './CustomerInfo';
import EnquiryDetails from './EnquiryDetails';
import ProjectNotes from './ProjectNotes';
import ProjectManagement from './ProjectManagement';
import InvoicesTab from './InvoicesTab';


const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { jeweller, currentUser, loading: authLoading } = useAuth();
  
  // Log to see what useAuth actually returns
  console.log('Jeweller:', jeweller);
  console.log('CurrentUser:', currentUser);
  
  // State management
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('enquiry');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // const [showArchiveModal, setShowArchiveModal] = useState(false);
  
  // Computed values
  const isStripeConnected = jeweller?.stripe?.status === "connected";
  const isProjectStarted = project?.status === 'project_started' || project?.status === 'project_completed';

  // Fetch project data on mount
  useEffect(() => {
    const fetchProjectData = async () => {
      console.log('ProjectDetails - currentUser:', currentUser);
      console.log('ProjectDetails - currentUser uid:', currentUser?.uid);
      console.log('ProjectDetails - projectId:', projectId);
      
      // Wait for auth to be ready
      if (authLoading) {
        console.log('Auth is still loading...');
        return;
      }
      
      if (!currentUser?.uid) {
        console.log('No user found after auth loaded');
        setLoading(false);
        setError('Please log in to view this project');
        return;
      }

      setLoading(true);
      try {
        const projectRef = doc(db, 'projects', projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (projectDoc.exists()) {
          const projectData = { id: projectDoc.id, ...projectDoc.data() };
          console.log('Project data fetched:', projectData);
          
          // Check if this user owns this project
          if (projectData.jewellerId !== currentUser?.uid && projectData.jewellerId !== jeweller?.id) {
            console.error('Jeweller does not own this project');
            setError('You do not have permission to view this project');
            setLoading(false);
            return;
          }
          
          setProject(projectData);

          // Fetch related invoices if they exist
          if (projectData.invoiceIds && projectData.invoiceIds.length > 0) {
            const invoicesQuery = query(
              collection(db, 'invoices'),
              where('__name__', 'in', projectData.invoiceIds)
            );
            const invoicesSnapshot = await getDocs(invoicesQuery);
            const invoicesData = invoicesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setInvoices(invoicesData);
          }
        } else {
          setError('Project not found');
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
        setError('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [currentUser, projectId, authLoading]); // Update dependencies

  // Handle project status updates
  const updateProjectStatus = async (projectId, newStatus) => {
    setUpdatingStatus(true);
    
    try {
      const updateStatus = httpsCallable(functions, 'updateProjectStatus');
      await updateStatus({ projectId, newStatus });
      
      // Update local state optimistically
      setProject(prev => ({
        ...prev,
        status: newStatus,
        stage: {
          ...prev.stage,
          current: newStatus,
          [`${newStatus}Date`]: new Date()
        }
      }));

      // Reset tab if moving to project_started
      if (newStatus === 'project_started') {
        setActiveTab('project');
      }

      return true;
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update project status. Please try again.');
      return false;
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle file uploads for product images
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File ${file.name} exceeds 10MB limit`);
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${file.name} is not an image`);
        }

        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `projects/${projectId}/products/${fileName}`);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          name: file.name,
          url: downloadURL,
          uploadedAt: new Date(),
          size: file.size,
          type: file.type
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Persist metadata via backend callable to satisfy strict rules
      const addImages = httpsCallable(functions, 'addProjectImages');
      await addImages({ projectId, images: uploadedFiles });

      // Update local state
      setProject(prev => ({
        ...prev,
        productImages: [...(prev.productImages || []), ...uploadedFiles]
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(error.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle notes changes with auto-save
  const handleNotesChange = async (value) => {
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        notes: value,
        updatedAt: new Date()
      });
      
      // Update local state
      setProject(prev => ({ ...prev, notes: value }));
    } catch (error) {
      console.error('Error updating notes:', error);
      // Don't show alert for notes as they auto-save
    }
  };

  // Handle project archiving
  // Archive flows temporarily disabled
  // const handleArchiveProject = async () => {
  //   setShowArchiveModal(false);
  //   const destination = isProjectStarted ? '/projects' : '/enquiries';
  //   
  //   const statusUpdated = await updateProjectStatus(projectId, 'archived');
  //   if (statusUpdated) {
  //     navigate(destination);
  //   }
  // };

  // const handleUnarchiveProject = async () => {
  //   const statusUpdated = await updateProjectStatus(projectId, 'in_conversation');
  //   if (statusUpdated) {
  //     navigate('/enquiries');
  //   }
  // };

  // Loading state (including auth loading)
  if (loading || authLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorText}>
          <p className={styles.errorTitle}>Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // No project found
  if (!project) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Project not found</p>
      </div>
    );
  }

  // Main render
  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Header Card - Separate from main content */}
        <ProjectHeader
          project={project}
          onStatusUpdate={updateProjectStatus}
          updatingStatus={updatingStatus}
        />

        {/* Timeline Card - Shows project progression */}
        <ProjectTimeline project={project} />

        {/* Main Content Card */}
        <div className={styles.mainCard}>
          <ProjectTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isProjectStarted={isProjectStarted}
            isStripeConnected={isStripeConnected}
            project={project}
            updateProjectStatus={updateProjectStatus}
            updatingStatus={updatingStatus}
          />

          {/* Content Area */}
          <div className={styles.contentArea}>
            {/* Enquiry Tab Content */}
            {activeTab === 'enquiry' && (
              <div className={styles.enquiryGrid}>
                <div className={styles.enquiryMain}>
                  <CustomerInfo 
                    customer={project.customerInfo} 
                    enquiryDetails={project.enquiryDetails}
                    projectId={projectId}
                    onCustomerUpdate={(updatedCustomer) => {
                      setProject((prev) => ({
                        ...prev,
                        customerInfo: updatedCustomer,
                      }));
                    }}
                  />
                  {project.enquiryDetails && (
                    <EnquiryDetails 
                      enquiryDetails={project.enquiryDetails}
                      projectId={projectId}
                    />
                  )}
                </div>
                <div className={styles.enquirySidebar}>
                  <ProjectNotes
                    notes={project.notes}
                    onChange={handleNotesChange}
                  />
                </div>
              </div>
            )}

            {/* Project Management Tab Content */}
            {activeTab === 'project' && isProjectStarted && (
              <ProjectManagement
                project={project}
                onFileUpload={handleFileUpload}
                uploading={uploading}
                invoices={invoices}
                onImagesUpdate={(updatedImages) => {
                  setProject((prev) => ({
                    ...prev,
                    productImages: updatedImages
                  }));
                }}
              />
            )}

            {/* Invoices Tab Content */}
              {activeTab === 'invoices' && isProjectStarted && (
                isStripeConnected ? (
                  <InvoicesTab 
                    project={project} 
                    invoices={invoices}
                  />
                ) : (
                  <div className={styles.setupBlock}>
                    <PaymentSetupCard />
                  </div>
                )
              )}
          </div>
        </div>

        {/* Archive Button - Moved below content */}
        {/* Archive actions temporarily hidden until behavior clarified */}
      </div>
    </div>
  );
};

export default ProjectDetails;
